/**
 * Tema de la interfaz con tres estados: `sistema` sigue al sistema operativo y reacciona en vivo;
 * los otros dos lo ignoran. El valor vive en localStorage, y `data-theme` solo toma `light` o
 * `dark`, los únicos que definen tokens.
 */
export type Tema = 'claro' | 'oscuro' | 'sistema';

const CLAVE = 'tanteo-tema';
const CONSULTA_OSCURO = '(prefers-color-scheme: dark)';

/** Lo que el atributo del documento entiende, para cada elección. */
const ATRIBUTO: Record<Exclude<Tema, 'sistema'>, 'light' | 'dark'> = { claro: 'light', oscuro: 'dark' };

export function temaGuardado(): Tema {
  const valor = localStorage.getItem(CLAVE);
  // `light`/`dark` son los valores guardados por versiones anteriores del selector.
  if (valor === 'claro' || valor === 'light') return 'claro';
  if (valor === 'oscuro' || valor === 'dark') return 'oscuro';
  return 'sistema';
}

function resolver(tema: Tema): 'light' | 'dark' {
  if (tema === 'sistema') return window.matchMedia(CONSULTA_OSCURO).matches ? 'dark' : 'light';
  return ATRIBUTO[tema];
}

function aplicarTema(tema: Tema): void {
  document.documentElement.setAttribute('data-theme', resolver(tema));
}

export function fijarTema(tema: Tema): void {
  localStorage.setItem(CLAVE, tema);
  aplicarTema(tema);
}

/**
 * Mientras la elección sea `sistema`, un cambio de tema del sistema operativo se refleja sin
 * recargar. Devuelve la función que cancela la suscripción.
 */
export function seguirAlSistema(): () => void {
  const consulta = window.matchMedia(CONSULTA_OSCURO);
  const alCambiar = () => {
    if (temaGuardado() === 'sistema') aplicarTema('sistema');
  };
  consulta.addEventListener('change', alCambiar);
  return () => consulta.removeEventListener('change', alCambiar);
}
